import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import UserAuth from "../../../auth/userAuth";
import { searchDocuments as searchDocumentsAPI, getSearchRecordsCount as getSearchRecordsCountAPI } from "../../api/documentsAPI";
import { serializeError } from "../../utils/serializeError";
import { trackRequest } from "../../utils/requestTracker";

const userAuth = new UserAuth();

/**
 * Dedup keys include refreshTrigger so a forced refresh (e.g. after check-in existing) always
 * starts a new HTTP call. Otherwise trackRequest would return the same in-flight promise when an
 * earlier get-search with identical params was still pending (e.g. user left All Documents while
 * the list was loading).
 */
function getSearchRequestKey(params, refreshTrigger) {
  return `documentSearch-${JSON.stringify(params)}-rt${refreshTrigger ?? 0}`;
}

function getSearchCountRequestKey(params, refreshTrigger) {
  return `documentSearchCount-${JSON.stringify(params)}-rt${refreshTrigger ?? 0}`;
}

/**
 * Search documents only. Calls api/get-search.
 * @param {object | number} paramsOrPageIndex - full search params, or pageIndex (number) using last search params
 * @returns {Promise<any>} search response data
 */
export const searchDocumentsThunk = createAsyncThunk(
  "documentSearch/search",
  async (paramsOrPageIndex, { getState, rejectWithValue }) => {
    const state = getState();
    const documentSearch = state.DocumentSearch ?? {};
    let params;
    if (typeof paramsOrPageIndex === "number") {
      const lastParams = documentSearch.lastSearchParams;
      if (!lastParams) {
        return rejectWithValue({ message: "No previous search; run a search first." });
      }
      params = { ...lastParams, pageIndex: paramsOrPageIndex };
    } else {
      params = paramsOrPageIndex;
    }
    try {
      const tokenResponse = await userAuth.getUserToken(true);
      const accessToken = tokenResponse?.accessToken ?? null;
      if (!accessToken) {
        return rejectWithValue({ message: "No access token" });
      }
      const refreshTrigger = getState().DocumentSearch?.refreshTrigger ?? 0;
      const key = getSearchRequestKey(params, refreshTrigger);
      const data = await trackRequest(key, () => searchDocumentsAPI(params, accessToken).then((res) => res.data));
      return data;
    } catch (error) {
      return rejectWithValue(serializeError(error));
    }
  }
);

/**
 * Fetch total record count for pagination. Calls api/get-search-records-count.
 * @param {object} params - same search params as search (tenantId, filters, etc.)
 * @returns {Promise<{ value: number }>}
 */
export const fetchSearchCountThunk = createAsyncThunk(
  "documentSearch/fetchCount",
  async (params, { getState, rejectWithValue }) => {
    if (!params || typeof params !== "object") {
      return rejectWithValue({ message: "Params required for count" });
    }
    try {
      const tokenResponse = await userAuth.getUserToken(true);
      const accessToken = tokenResponse?.accessToken ?? null;
      if (!accessToken) {
        return rejectWithValue({ message: "No access token" });
      }
      const refreshTrigger = getState().DocumentSearch?.refreshTrigger ?? 0;
      const key = getSearchCountRequestKey(params, refreshTrigger);
      const countRes = await trackRequest(key, () => getSearchRecordsCountAPI(params, accessToken));
      const value = countRes?.data?.value;
      return { value: value !== undefined && value !== null ? Number(value) : 0 };
    } catch (error) {
      return rejectWithValue(serializeError(error));
    }
  }
);

const initialState = {
  loading: true,
  countLoading: false,
  data: null,
  isError: false,
  message: "",
  selectedPageIndex: 0,
  pageSize: 20,
  totalCount: 0,
  lastSearchParams: null,
  /** Incremented when All Documents list should refetch (e.g. after check-in existing). */
  refreshTrigger: 0,
  /** Set after check-out; consumed when All Documents panel mounts (avoids get-search while on Working Documents). */
  pendingRefreshOnNextMount: false,
  /** Latest in-flight search thunk; ignore stale fulfilled/rejected for older requests. */
  latestSearchRequestId: null,
  latestCountRequestId: null,
};

const documentSearchSlice = createSlice({
  name: "documentSearch",
  initialState,
  reducers: {
    requestRefreshAllDocuments: (state) => {
      state.refreshTrigger = (state.refreshTrigger || 0) + 1;
    },
    markAllDocumentsStaleAfterCheckout: (state) => {
      state.pendingRefreshOnNextMount = true;
    },
    clearPendingAllDocumentsRefresh: (state) => {
      state.pendingRefreshOnNextMount = false;
    },
    clearSearch: (state) => {
      state.loading = false;
      state.countLoading = false;
      state.data = null;
      state.isError = false;
      state.message = "";
      state.selectedPageIndex = 0;
      state.totalCount = 0;
      state.lastSearchParams = null;
      state.latestSearchRequestId = null;
      state.latestCountRequestId = null;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload ?? 10;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchDocumentsThunk.pending, (state, action) => {
        state.loading = true;
        state.isError = false;
        state.message = "";
        state.latestSearchRequestId = action.meta.requestId;
      })
      .addCase(searchDocumentsThunk.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.latestSearchRequestId) {
          return;
        }
        state.loading = false;
        const payload = action.payload;
        const list = Array.isArray(payload) ? payload : (payload?.value ?? []);
        state.data = list;
        state.isError = false;
        state.message = "";
        const arg = action.meta?.arg;
        const pageIndex = typeof arg === "number" ? arg : arg?.pageIndex;
        if (pageIndex !== undefined && pageIndex !== null) {
          state.selectedPageIndex = pageIndex;
        }
        const params = typeof arg === "number" ? { ...state.lastSearchParams, pageIndex: arg } : arg;
        if (params && typeof params === "object") {
          state.lastSearchParams = params;
        }
      })
      .addCase(searchDocumentsThunk.rejected, (state, action) => {
        if (action.meta.requestId !== state.latestSearchRequestId) {
          return;
        }
        state.loading = false;
        state.data = null;
        state.isError = true;
        state.message = action.payload?.message ?? action.error?.message ?? "Search failed";
      })
      .addCase(fetchSearchCountThunk.pending, (state, action) => {
        state.countLoading = true;
        state.latestCountRequestId = action.meta.requestId;
      })
      .addCase(fetchSearchCountThunk.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.latestCountRequestId) {
          return;
        }
        state.countLoading = false;
        const value = action.payload?.value;
        if (value !== undefined && value !== null) {
          state.totalCount = Number(value);
        }
      })
      .addCase(fetchSearchCountThunk.rejected, (state, action) => {
        if (action.meta.requestId !== state.latestCountRequestId) {
          return;
        }
        state.countLoading = false;
      });
  },
});

export const {
  clearSearch,
  setPageSize,
  requestRefreshAllDocuments,
  markAllDocumentsStaleAfterCheckout,
  clearPendingAllDocumentsRefresh,
} = documentSearchSlice.actions;
export default documentSearchSlice;
